import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CollectionDocument } from '@/shared/types/collections';

export const useDownloadAsZip = () => {
  const { t } = useTranslation();

  const downloadAsZip = async (
    selectedItems: CollectionDocument[],
    collectionId: string,
    accountSlug: string
  ) => {
    if (selectedItems.length === 0) {
      return;
    }

    const zip = new JSZip();
    const toastId = toast.loading(
      t('collections.bulk_download.preparing_to_download', { 0: selectedItems.length }),
      { description: t('collections.bulk_download.fetching_documents') }
    );

    try {
      // Fetch all documents as blobs
      const documentPromises = selectedItems.map(async (item, index) => {
        const { document_id, datasource_id, name, type } = item;
        const url = `${window?.location?.origin}/www/api/v1/collections/${collectionId}/datasources/${datasource_id}/documents/${document_id}/download?account_slug=${accountSlug}`;

        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${name}`);
          }

          const blob = await response.blob();
          const fileName = name || `document_${index + 1}.${type}`;

          // Add file to zip
          zip.file(fileName, blob);

          return { success: true, fileName };
        } catch (error) {
          console.error(`Failed to download ${name}:`, error);
          return { success: false, fileName: name, error };
        }
      });

      // Wait for all downloads to complete
      const results = await Promise.all(documentPromises);

      // Check results
      const successful = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);

      if (successful.length === 0) {
        toast.error(t('collections.bulk_download.failed_to_download_any'), { id: toastId });
        return;
      }

      // Update toast to show zip creation
      toast.loading(t('collections.bulk_download.creating_zip_file'), { id: toastId });

      // Generate zip file
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      // Create filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const zipFileName = `documents_${timestamp}.zip`;

      // Download the zip file
      saveAs(zipBlob, zipFileName);

      // Show success message
      const message = t('collections.bulk_download.successfully_downloaded', {
        0: successful.length,
      });
      let description = t('collections.bulk_download.downloaded_as', { 0: zipFileName });

      if (failed.length > 0) {
        description += t('collections.bulk_download.failed_to_download', { 0: failed.length });
      }

      toast.success(message, { id: toastId, description });

      // Log failed downloads for debugging
      if (failed.length > 0) {
        console.warn('Failed downloads:', failed);
      }
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error(t('collections.bulk_download.failed_to_create_zip_file'), { id: toastId });
    }
  };

  return { downloadAsZip };
};
